import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { CourseLmsProfileType } from 'enum'
import CourseForm from 'components/ui/forms/course/CourseForm'
import { CourseFormPageTitle } from 'components/ui/forms/course/components/CourseFormPageTitle'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/courses.action'
import {
  ICourseModel,
  NewCourse,
  courseInitialState,
  newCourseProfilesOnlyUpdate,
} from 'models/course.model'
import Loader from 'components/ui/loader/Loader'
import { OnSubmitValues } from 'components/ui/forms/course/types'
import { useAccess } from 'contexts/AccessContext'
import Wrapper from 'components/reusables/wrapper'
import { NormativeCourseEditRetroactiveAlert } from 'components/ui/forms/course/components/forms/NormativeCourseEditRetroactiveAlert'
import { useProfiles } from 'components/ui/forms/course/hooks/useProfiles'
import { getSynchronousCourse } from '../utils'
import { EditCourseRetroactiveAlertSection } from './styled'

interface Params {
  courseId: string
}

export const Edit: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const { courseId } = useParams<Params>()

  const { course } = useSelector(({ courses }: IMainState) => courses)
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const { loading } = useSelector(({ courses }: IMainState) => courses)
  const { canAccessCourse, loading: accessLoading, access } = useAccess()
  const { profiles: allowedProfilesList } = useProfiles()
  const allowedProfileIds = useMemo(
    () => new Set(allowedProfilesList.map((profile) => Number(profile.value))),
    [allowedProfilesList]
  )

  useEffect(() => {
    dispatch({ type: fromActions.GET_ADMIN_COURSE, payload: courseId })
  }, [courseId, dispatch])

  useEffect(() => {
    if (
      !accessLoading &&
      (!access.canEditCourseFromList() || !canAccessCourse(Number(courseId)))
    ) {
      window.location.href = '/forbidden'
    }
  }, [access, accessLoading, canAccessCourse, courseId])

  const courseData = useMemo(() => course || courseInitialState, [course])

  const transformLmsProfiles = (lmsProfileId: number, kind: number) => {
    const data = { lmsProfileId, kind }
    if (course && course.coursesLmsProfiles) {
      const lmsProfile = course.coursesLmsProfiles.find(
        ({ lmsProfileId: existsLmsProfileId }) =>
          existsLmsProfileId === lmsProfileId
      )
      if (lmsProfile) {
        return { ...data, id: Number(lmsProfile.id) }
      }
    }
    return data
  }

  const canAssignProfiles = access.canAssignCourseProfiles()
  const canEditCourseContent = access.can('manageInternalCourses')
  const profilesOnlyEdit = canAssignProfiles && !canEditCourseContent
  const omitProfilesOnSave = !canAssignProfiles

  const handleSubmit = (values: OnSubmitValues, onlySave: boolean) => {
    let coursesLmsProfilesAttributes: any[] = []

    let auxOptional = values.optionalProfiles || []
    auxOptional = auxOptional.map(({ value: profileId }: any) =>
      transformLmsProfiles(profileId, CourseLmsProfileType.Optional)
    )

    let auxRequired = values.requiredProfiles || []
    auxRequired = auxRequired.map(({ value: profileId }: any) =>
      transformLmsProfiles(profileId, CourseLmsProfileType.Required)
    )

    coursesLmsProfilesAttributes = auxRequired.concat(auxOptional)

    if (course && course.coursesLmsProfiles) {
      const removeLms = course.coursesLmsProfiles
        .map((lmsProfile) => {
          if (
            !coursesLmsProfilesAttributes.some(
              ({ lmsProfileId }) => lmsProfileId === lmsProfile.lmsProfileId
            )
          ) {
            if (!allowedProfileIds.has(Number(lmsProfile.lmsProfileId))) {
              return null
            }
            return { ...lmsProfile, _destroy: true }
          }
          return null
        })
        .filter((e) => e)
      if (removeLms.length) {
        coursesLmsProfilesAttributes = [
          ...coursesLmsProfilesAttributes,
          ...removeLms,
        ]
      }
    }

    const saveOnlyProfiles = profilesOnlyEdit
    const synchronousCourse = saveOnlyProfiles
      ? undefined
      : getSynchronousCourse(values)

    const profileAttributes = omitProfilesOnSave
      ? undefined
      : coursesLmsProfilesAttributes

    const saveCourse = saveOnlyProfiles
      ? newCourseProfilesOnlyUpdate(coursesLmsProfilesAttributes)
      : NewCourse({
          name: values.name,
          description: values.description,
          courseType: values.courseType,
          state: values.state?.value,
          startDate: values.startDate ? String(values.startDate) : '',
          untilDate: values.untilDate ? String(values.untilDate) : '',
          banner: values.banner,
          thumbnail: values.thumbnail,
          ...(profileAttributes
            ? { coursesLmsProfilesAttributes: profileAttributes }
            : {}),
          invitationNotification: values.invitationNotification,
          canGenerateCertificate: values.canGenerateCertificate,
          reminderNotification: values.reminderNotification,
          reminderNoStartCourseNotification:
            values.reminderNoStartCourseNotification,
          enrolCreatedNotification: values.enrolCreatedNotification,
          synchronousCourse,
          hours: values.hours,
          canRestart: values.canRestart ?? false,
          integrations: values.integrations,
          normative: values.normative,
        })

    dispatch({
      type: fromActions.EDIT_COURSE,
      payload: {
        course: saveCourse as ICourseModel,
        courseId,
        onlySave: saveOnlyProfiles ? true : onlySave,
      },
    })
  }

  const handleCancel = () => history.push('/administration/courses/')

  return loading || accessLoading ? (
    <Loader overlay color />
  ) : (
    <Wrapper shadow={true}>
      <CourseFormPageTitle
        title={t('administration.courses.edit.pageTitle')}
        onBack={handleCancel}
      />
      {attestationsEnabled && courseData.normative ? (
        <EditCourseRetroactiveAlertSection>
          <NormativeCourseEditRetroactiveAlert />
        </EditCourseRetroactiveAlertSection>
      ) : null}
      <CourseForm initialValues={courseData} onSubmit={handleSubmit} />
    </Wrapper>
  )
}

export default Edit
